import { track } from "@/features/analytics";
import { useCallback, useEffect, useRef, useState } from "react";
import { useFileToStorydocStore } from "../stores";
import {
  DRIVE_ACCESS_MESSAGE,
  DRIVE_FETCH_MESSAGE,
  DriveImportError,
  type PickedDriveFile,
  pickDriveFile,
  validateDrivePick,
} from "../utils/pick-drive-file";
import { trackImport } from "../utils/track-import";

export const useDrivePick = (
  onPicked: (picked: PickedDriveFile) => void | Promise<void>,
) => {
  const [pending, setPending] = useState(false);
  const pendingRef = useRef(false);
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const pickFromDrive = useCallback(async () => {
    if (pendingRef.current) return;
    pendingRef.current = true;
    setPending(true);
    const s = useFileToStorydocStore.getState();
    try {
      const picked = await pickDriveFile();
      // the Picker outlives the dialog; a pick after close must not start an upload
      if (!picked || !mountedRef.current) return;
      const check = validateDrivePick(picked);
      if (!check.ok) {
        s.setInlineError(check.message);
        trackImport("error", {
          source: "drive",
          importJobId: null,
          stage: "select",
          code: check.reason,
        });
        return;
      }
      s.setInlineError(null);
      track("select pdf to storydoc file");
      trackImport("source chosen", { source: "drive", importJobId: null });
      void onPicked(picked);
    } catch (error) {
      if (!mountedRef.current) return;
      const driveError =
        error instanceof DriveImportError
          ? error
          : new DriveImportError("fetch", "drive-picker", "unknown");
      s.setInlineError(
        driveError.code === "access"
          ? DRIVE_ACCESS_MESSAGE
          : DRIVE_FETCH_MESSAGE,
        "drive",
      );
      trackImport("error", {
        source: "drive",
        importJobId: null,
        stage: driveError.stage,
        code: driveError.detail,
      });
    } finally {
      pendingRef.current = false;
      if (mountedRef.current) setPending(false);
    }
  }, [onPicked]);

  return { pickFromDrive, pending };
};
